import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/utils';

/**
 * Account picker for users who belong to more than one account.
 * Hidden entirely when accounts.length === 1, which is the common
 * case. Switching mints a new session JWT (server-side) and refreshes
 * the AuthContext.
 */
export default function AccountSwitcher() {
  const { account, accounts, switchAccount } = useAuth();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!account || accounts.length <= 1) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="account-switcher"
      >
        <span className="truncate max-w-[160px]">{account.name}</span>
        <ChevronDown className="h-3 w-3" aria-hidden />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute left-0 z-40 mt-2 w-56 overflow-hidden rounded-md border border-rule bg-card shadow-lg"
        >
          <ul className="py-1">
            {accounts.map((a) => {
              const active = a.id === account.id;
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    disabled={pending}
                    onClick={async () => {
                      if (active) return setOpen(false);
                      setPending(true);
                      try {
                        await switchAccount(a.id);
                        // Hard reload. SPA navigate('/') is a no-op
                        // when you're already on the dashboard, which
                        // leaves the just-cleared TanStack queries
                        // without observers to trigger a refetch.
                        // A full reload guarantees a clean component
                        // tree + fresh /me hydration from the new
                        // session cookie.
                        window.location.assign('/');
                      } finally {
                        setPending(false);
                        setOpen(false);
                      }
                    }}
                    className={cn(
                      'flex w-full items-center justify-between gap-3 px-3 py-2 text-sm text-left hover:bg-muted disabled:opacity-50',
                      active && 'bg-accent/50',
                    )}
                  >
                    <span className="truncate">{a.name}</span>
                    {active ? <Check className="h-4 w-4 text-primary shrink-0" aria-hidden /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
